import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { DatePicker, Input, RadioButtons, Select } from '@design-system';
import { parseISO } from 'date-fns';
import classNames from 'classnames';
import { IOption, SelectChangeVariant } from '@design-system/elements/Select/Select.types';
import { InputChangeVariant } from '@design-system/elements/Input/Input.types';
import { DatePickerChangeVariant } from '@design-system/elements/DatePicker/DatePicker.types';
import { useGetCountries } from 'src/shared/queries/useGetCountries';
import { useGetStates } from 'src/shared/queries/useGetStates';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { useGetCounties } from 'src/shared/queries/useGetCounties';
import { ICompanyWorkLocation } from 'src/shared/types/Company';
import { ContactAddress } from 'src/shared/types/User';
import { useGetReferenceData } from 'src/shared/queries/useGetReferenceData';
import { EmptyDateValue } from 'src/utils/constants/common';

export const InitialWorkLocation: ICompanyWorkLocation = {
  workLocationType: '',
  clientName: '',
  country: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  unitType: '',
  suite: '',
  state: '',
  zipCode: '',
  county: '',
  startDate: '',
  endDate: '',
};

interface IWorkLocation {
  refLocation: ContactAddress;
  workLocation: ContactAddress;
  corporateLocations: ICompanyWorkLocation[];
  onChange: (location: ContactAddress) => void;
  disabled?: boolean;
}

const WORK_LOCATION_TYPES = {
  THIRD_PARTY_WORKSITE: { hideBox: false, boxTitle: 'Client Name', fieldName: 'clientName' },
  CORPORATE_WORKSITE: { hideBox: false, boxTitle: 'Corporate Work Location', fieldName: '' },
  WORK_FROM_HOME: { hideBox: true, boxTitle: '', fieldName: '' },
  '': { hideBox: true, boxTitle: '', fieldName: '' },
};

type WorkLocationTypeKey = keyof typeof WORK_LOCATION_TYPES;

const WorkLocation = ({ refLocation, workLocation, corporateLocations, onChange, disabled = false }: IWorkLocation) => {
  const { data: locationTypes } = useGetReferenceData('WORK_LOCATION_TYPE');
  const { data: user } = useGetUser();
  const { data: countries } = useGetCountries();
  const { data: states } = useGetStates(workLocation?.country ?? '');
  const { data: counties } = useGetCounties(workLocation.state ?? '');
  const [corporateLocationOptions, setCorporateLocationOptions] = useState<IOption[]>([]);
  const isWorkLocationDirty = useRef(false);
  const dateFormat = user?.userSettings?.dateFormat || '';

  useEffect(() => {
    const options: IOption[] =
      corporateLocations
        ?.map((location, index): IOption | null => {
          const labelParts: string[] = [];

          if (location.addressLine1) labelParts.push(location.addressLine1);
          if (location.addressLine2) labelParts.push(location.addressLine2);
          if (location.suite) labelParts.push(location.suite);
          if (location.city) labelParts.push(location.city);
          if (location.state) labelParts.push(location.state);
          if (location.country) labelParts.push(location.country);
          if (location.zipCode) labelParts.push(location.zipCode);

          const label = labelParts.join(', ').trim();
          const value = index;

          if (label !== '') {
            return { value, label };
          } else {
            return null;
          }
        })
        .filter((option): option is IOption => option !== null) || [];
    setCorporateLocationOptions(options);
  }, [corporateLocations]);

  const handleDateChange = (fieldName: string, value: string | Date | null) => {
    const date = value ? new Date(value) : EmptyDateValue;
    handleFieldChange(fieldName, date);
  };

  const handleFieldChange = (fieldName: string, value: IOption | string | number | boolean | Date) => {
    isWorkLocationDirty.current = true;
    let updatedWorkLocation = workLocation;

    if (value instanceof Date) {
      const formattedDate = `${value.getFullYear()}-${(value.getMonth() + 1).toString().padStart(2, '0')}-${value
        .getDate()
        .toString()
        .padStart(2, '0')}`;
      updatedWorkLocation = {
        ...updatedWorkLocation,
        [fieldName]: formattedDate,
      };
    } else if (value && typeof value === 'object' && 'value' in value) {
      updatedWorkLocation = { ...updatedWorkLocation, [fieldName]: (value.value as string) || '' };
    } else {
      updatedWorkLocation = { ...updatedWorkLocation, [fieldName]: (value as string) || '' };
    }

    if (fieldName === 'country') {
      updatedWorkLocation = { ...updatedWorkLocation, state: '' };
    }
    onChange(updatedWorkLocation);
  };

  const handleWorkLocationFieldChange = (fieldName: string) => (event: ChangeEvent<HTMLInputElement>) => {
    isWorkLocationDirty.current = true;
    onChange({ ...workLocation, [fieldName]: event.target.value });
  };

  const handleCorporateWorksiteChange = (data: IOption) => {
    isWorkLocationDirty.current = true;
    onChange({ ...workLocation, ...corporateLocations[data.value as number] });
  };

  const memoizedCountries = useMemo(
    () =>
      countries?.map(({ countryCode, countryName }) => ({
        label: countryName,
        value: countryCode,
      })) || [],
    [countries]
  );

  const memorizedLocationTypes = useMemo(
    () => locationTypes?.map((location) => ({ value: location.code, label: location.description })) || [],
    [locationTypes]
  );

  const memoizedStates = useMemo(
    () => states?.map(({ stateName }) => ({ label: stateName, value: stateName })) || [],
    [states]
  );

  const memoizedCounties = useMemo(
    () => counties?.map((countyName) => ({ label: countyName, value: countyName })) || [],
    [counties]
  );

  const {
    workLocationType,
    country,
    state,
    county,
    clientName,
    addressLine1,
    addressLine2,
    city,
    unitType,
    suite,
    zipCode,
    startDate,
    endDate,
  } = workLocation;

  const locationType = (workLocation[
    WORK_LOCATION_TYPES[workLocationType as WorkLocationTypeKey]?.fieldName as keyof ContactAddress
  ] || '') as string;

  const styles = {
    container: 'w-full flex flex-col gap-[32px] py-[24px] lg:pt-[0]',
    row: 'flex flex-col lg:flex-row gap-[32px] lg:gap-[0px]',
    col3: 'w-full lg:w-1/4 lg:pr-[24px]',
    col6: 'w-full lg:w-1/2 lg:pr-[24px]',
    col9: 'w-full lg:w-3/4 lg:pr-[24px]',
  };

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <div className={styles.col3}>
          <Select
            label="Type of Work Location"
            options={memorizedLocationTypes}
            selectedOption={{ value: workLocationType || '', label: workLocationType || '' }}
            onChange={(option) => handleFieldChange('workLocationType', option as IOption)}
            searchable
            showClear={workLocationType ? true : false}
            hasChanged={
              !!refLocation &&
              memorizedLocationTypes?.length > 0 &&
              (workLocationType || '') !== (refLocation.workLocationType || '')
            }
            disabled={disabled}
            selectChangedStyle={SelectChangeVariant.Secondary}
          />
        </div>
        {workLocationType && !WORK_LOCATION_TYPES[workLocationType as WorkLocationTypeKey].hideBox ? (
          <div className={styles.col3}>
            {workLocationType === 'CORPORATE_WORKSITE' ? (
              <Select
                label={WORK_LOCATION_TYPES[workLocationType].boxTitle}
                options={corporateLocationOptions || null}
                onChange={(option) => handleCorporateWorksiteChange(option as IOption)}
                selectedOption={{ label: '', value: '' }}
                searchable
                disabled={disabled}
                selectChangedStyle={SelectChangeVariant.Secondary}
              />
            ) : (
              <Input
                className="location-input"
                label={WORK_LOCATION_TYPES[workLocationType as WorkLocationTypeKey].boxTitle}
                placeholder={WORK_LOCATION_TYPES[workLocationType as WorkLocationTypeKey].boxTitle}
                onChange={handleWorkLocationFieldChange('clientName')}
                value={locationType || ''}
                hasChanged={!!refLocation && (clientName || '') !== (refLocation.clientName || '')}
                disabled={disabled}
                inputChangedStyle={InputChangeVariant.Secondary}
              />
            )}
          </div>
        ) : null}
        <div className={styles.col3}>
          <Select
            label="Country / Location"
            options={memoizedCountries}
            selectedOption={{ value: country || '', label: country || '' }}
            onChange={(option) => handleFieldChange('country', option as IOption)}
            searchable
            showClear={country ? true : false}
            hasChanged={
              !!refLocation && memoizedCountries?.length > 0 && (country || '') !== (refLocation.country || '')
            }
            disabled={disabled}
            selectChangedStyle={SelectChangeVariant.Secondary}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.col6}>
          <Input
            className="location-input"
            label="Address Line 1"
            value={addressLine1 || ''}
            onChange={handleWorkLocationFieldChange('addressLine1')}
            hasChanged={!!refLocation && (addressLine1 || '') !== (refLocation.addressLine1 || '')}
            disabled={disabled}
            inputChangedStyle={InputChangeVariant.Secondary}
          />
        </div>
        <div className={styles.col6}>
          <Input
            className="location-input"
            label="Address Line 2"
            value={addressLine2 || ''}
            onChange={handleWorkLocationFieldChange('addressLine2')}
            hasChanged={!!refLocation && (addressLine2 || '') !== (refLocation.addressLine2 || '')}
            disabled={disabled}
            inputChangedStyle={InputChangeVariant.Secondary}
          />
        </div>
      </div>
      <div className={classNames(styles.row, 'items-end')}>
        <div className={styles.col3}>
          <Input
            className="location-input"
            label="City"
            value={city || ''}
            onChange={handleWorkLocationFieldChange('city')}
            hasChanged={!!refLocation && (city || '') !== (refLocation.city || '')}
            disabled={disabled}
            inputChangedStyle={InputChangeVariant.Secondary}
          />
        </div>
        <div className={classNames(styles.col3, 'flex flex-col gap-[10px]')}>
          <div>
            <RadioButtons
              name="unitType"
              options={[
                { label: 'Floor', value: 'FLOOR' },
                { label: 'Apt', value: 'APARTMENT' },
                { label: 'Suite', value: 'SUITE' },
              ]}
              selectedOption={unitType}
              onChange={(option) => handleFieldChange('unitType', option)}
              direction="row"
              type={refLocation?.unitType == unitType ? 'secondary' : 'highlighted'}
              disabled={disabled}
            />
          </div>
          <div>
            <Input
              className="location-input"
              label="Floor / Apt / Suite"
              value={suite || ''}
              onChange={handleWorkLocationFieldChange('suite')}
              hasChanged={!!refLocation && (suite || '') !== (refLocation.suite || '')}
              disabled={disabled}
              inputChangedStyle={InputChangeVariant.Secondary}
            />
          </div>
        </div>
        <div className={styles.col3}>
          {memoizedStates?.length > 0 ? (
            <Select
              label="State / Province"
              options={memoizedStates}
              selectedOption={{ value: state || '', label: state || '' }}
              onChange={(option) => handleFieldChange('state', option as IOption)}
              searchable
              showClear={state ? true : false}
              hasChanged={!!refLocation && memoizedStates?.length > 0 && (state || '') !== (refLocation.state || '')}
              disabled={disabled}
              selectChangedStyle={SelectChangeVariant.Secondary}
            />
          ) : (
            <Input
              className="location-input"
              label="State / Province"
              value={state || ''}
              onChange={handleWorkLocationFieldChange('state')}
              hasChanged={!!refLocation && (state || '') !== (refLocation.state || '')}
              disabled={disabled}
              inputChangedStyle={InputChangeVariant.Secondary}
            />
          )}
        </div>
        <div className={styles.col3}>
          <Input
            className="location-input"
            label="Zip / Postal Code"
            value={zipCode || ''}
            onChange={handleWorkLocationFieldChange('zipCode')}
            hasChanged={!!refLocation && (zipCode || '') !== (refLocation.zipCode || '')}
            disabled={disabled}
            inputChangedStyle={InputChangeVariant.Secondary}
          />
        </div>
      </div>
      {workLocation?.country === 'US' ? (
        <div className={styles.row}>
          <div className={styles.col3}>
            {memoizedCounties?.length > 0 ? (
              <Select
                label="County"
                options={memoizedCounties}
                selectedOption={{ value: county || '', label: county || '' }}
                onChange={(option) => handleFieldChange('county', option as IOption)}
                searchable
                showClear={county ? true : false}
                hasChanged={
                  !!refLocation && memoizedCounties?.length > 0 && (county || '') !== (refLocation.county || '')
                }
                disabled={disabled}
                selectChangedStyle={SelectChangeVariant.Secondary}
              />
            ) : (
              <Input
                className="location-input"
                label="County"
                value={county || ''}
                hasChanged={!!refLocation && (county || '') !== (refLocation.county || '')}
                disabled
              ></Input>
            )}
          </div>
        </div>
      ) : null}
      <div className={styles.row}>
        <div className={styles.col3}>
          <DatePicker
            label="Work Location Start Date"
            dateFormat={dateFormat}
            value={startDate === EmptyDateValue || !startDate ? '' : startDate}
            onChange={(date) => handleDateChange('startDate', date)}
            maxDate={parseISO(endDate || '')}
            hasChanged={
              !!refLocation &&
              (startDate === EmptyDateValue || !startDate ? '' : startDate) !== (refLocation.startDate || '')
            }
            disabled={disabled}
            datePickerChangedStyle={DatePickerChangeVariant.Secondary}
          />
        </div>
        <div className={styles.col3}>
          <DatePicker
            label="Work Location End Date"
            dateFormat={dateFormat}
            value={endDate === EmptyDateValue || !endDate ? '' : endDate}
            onChange={(date) => handleDateChange('endDate', date)}
            minDate={parseISO(startDate || '')}
            hasChanged={
              !!refLocation && (endDate === EmptyDateValue || !endDate ? '' : endDate) !== (refLocation.endDate || '')
            }
            disabled={disabled}
            datePickerChangedStyle={DatePickerChangeVariant.Secondary}
          />
        </div>
      </div>
    </div>
  );
};

export default WorkLocation;
